import { comparer, makeAutoObservable, runInAction } from "mobx";
import type { SortingState } from "@tanstack/react-table";
import { type AnalyticsSource, track } from "@/lib/analytics";
import {
  isSortingState,
  readTablePref,
  writeTablePref,
  type TablePrefsKey,
} from "@/components/data-table/table-prefs-storage";
import { tablePrefsEnabledNow } from "@/components/data-table/table-prefs-gate";
import type { RootStore } from "./root.store";
import type {
  FieldListResponse,
  Report,
  ReportCreateBody,
  ReportObjectType,
  ReportRun,
  ReportSnapshotRow,
  ReportUpdateBody,
  SnapshotParams,
} from "@/types/report";

/** Stable cache key for a field-catalog request: normalized object-type set. */
function fieldsKey(objectTypes: ReportObjectType[]): string {
  return [...objectTypes].sort().join(",");
}

/** Rows per snapshot page. The table pages through the run server-side. */
const SNAPSHOT_PER_PAGE = 100;

/** Storage id for the Report View's snapshot table — must stay in step with the
 *  `tableId` that view hands `<DataTable>` (`report-view-page.tsx`), which is
 *  what the column prefs are already filed under. */
const SNAPSHOT_TABLE_ID = "report-snapshot";

/** Poll interval (ms) while a run is `pending`/`running`. */
const RUN_POLL_INTERVAL_MS = 2000;

/**
 * The PATCH keys whose value differs from the stored report: `report_updated`'s
 * `fields`. The builder sends every field on every save, so the body's keys alone
 * would name all of them each time. A report that is not in the map yet (an edit
 * deep-linked before the index loaded) counts every key it sends.
 */
function changedReportFields(body: ReportUpdateBody, before: Report | undefined): string[] {
  return (Object.keys(body) as (keyof ReportUpdateBody)[]).filter(
    (key) => !before || !comparer.structural(body[key], before[key]),
  );
}

/** A run whose status is still settling (the table shows a generating state). */
function runInProgress(run: ReportRun | undefined): boolean {
  return run?.status === "pending" || run?.status === "running";
}

/**
 * MobX store for the Reports feature (Insights → Reports). Follows the
 * dashboards store shape: an observable collection keyed by id, CRUD actions
 * that delegate to `ReportService`, and per-resource loading/error flags.
 * Reports are private to their owner (server-side scoped).
 */
export class ReportStore {
  rootStore: RootStore;

  reports = new Map<string, Report>();
  /** Server-reported total for the reports list, from the list response. */
  reportsTotalCount = 0;

  /** Field catalog cached by normalized object-type set (see `fieldsKey`). */
  fieldsByObjectTypes = new Map<string, FieldListResponse>();

  /**
   * Whether the report builder modal is open. Owned here (not in route-local
   * state) because the builder is launched from multiple surfaces — the index's
   * "Create report" button (issue 1674) and the row "Edit" action (issue 1675)
   * — and issue 1676 (the builder shell) is its consumer. The index sets this to
   * open the builder; until 1676 lands the builder renders nothing, so opening is a
   * structurally-wired no-op rather than a dangling TODO.
   */
  builderOpen = false;

  /**
   * The id of the report the builder is editing, or `null` for a create flow.
   * Set by `openBuilder(reportId)` from the row "Edit" action (issue 1675) so
   * the builder shell (issue 1676) knows whether to fetch + pre-populate an
   * existing report (the actual pre-population is issue 1679). It is a wired
   * hook, not a loose TODO: the value is owned + reset here, and its consumer
   * lands with the builder.
   */
  builderReportId: string | null = null;

  /**
   * The step the builder should open on (0-based). Defaults to the first step
   * (Report type) for Create and the row "Edit" action; the Report View's
   * "Edit filters" entry point opens directly on the dedicated Filters step
   * (index 2, #3185) by passing `{ step: 2 }` to `openBuilder`. Reset on close.
   */
  builderInitialStep = 0;

  /**
   * Whether the builder was opened from the Report View detail page (its Edit /
   * Edit-filters entries) rather than the index row "Edit" or Create. When true
   * the Name & save step offers a single "Save & view" confirm that also kicks
   * off a fresh run, so the user returns to the detail page seeing their edits
   * reflected. Reset on close.
   */
  builderFromDetail = false;

  loadingReports = false;
  loadingReport = new Map<string, boolean>();
  loadingFields = new Map<string, boolean>();

  errorReports: Error | null = null;
  errorReport = new Map<string, Error | null>();
  errorFields = new Map<string, Error | null>();

  /**
   * Set to `true` when `fetchReport` 404s — the report doesn't exist or belongs
   * to another user (the BE queryset is scoped to `owner=request.user`, so a
   * non-owner gets a 404, not a 403). The Report View shows a clean "not found /
   * no access" empty state rather than a perpetual title skeleton. This is the
   * FE side of the permissions contract (issue 1680): server-side ownership scoping
   * is the enforcement; the FE reflects it. Not an error flag — a 404 here is an
   * expected outcome, logged at `warn`.
   */
  reportNotFound = new Map<string, boolean>();

  // ---- Run + snapshot (Report View, issue 1678) ----------------------------
  // Keyed by report id so a navigation between two report views keeps each
  // report's run/rows independent (mirrors the per-id maps above).

  /** The latest run's metadata (status, columns, stale fields) per report. */
  runByReport = new Map<string, ReportRun>();

  /** The current snapshot page's rows per report. */
  snapshotRowsByReport = new Map<string, ReportSnapshotRow[]>();

  /** Server total row count for the loaded run's snapshot, per report. */
  snapshotTotalByReport = new Map<string, number>();

  /**
   * Cursor for the *next* snapshot page, per report (null when the loaded page
   * is the last). Captured from the OffsetPaginator envelope so `loadMoreSnapshot`
   * can page through the run; reset whenever the first page is (re)loaded.
   */
  snapshotCursorByReport = new Map<string, string | null>();

  /** Whether the snapshot has further pages beyond the loaded rows, per report. */
  snapshotHasMoreByReport = new Map<string, boolean>();

  /** True while a *subsequent* snapshot page is loading (load-more spinner), per report. */
  loadingMoreSnapshot = new Map<string, boolean>();

  /** Active server-side sort for the snapshot, per report (null = run default). */
  snapshotSortByReport = new Map<string, { sort: string; order: "asc" | "desc" }>();

  /** True while a run/snapshot fetch is in flight, per report. */
  loadingSnapshot = new Map<string, boolean>();

  /** True while a run is being triggered (the Re-run button's busy state), per report. */
  runningReport = new Map<string, boolean>();

  /**
   * Set to `true` when the snapshot 404s (the report has never been run), so the
   * Report View shows the "Run this report" prompt instead of a load error.
   */
  snapshotNeverRun = new Map<string, boolean>();

  /** Snapshot load error per report (excludes the never-run 404). */
  errorSnapshot = new Map<string, Error | null>();

  /** Active poll timer id per report (cleared when the run settles or on stop). */
  private pollTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * The workspace slug each hydrated report's stored sort belongs to, and so the
   * only key `persistSnapshotSort` may write for that report.
   *
   * Deliberately NOT `rootStore.router.workspaceSlug`, nor the slug the mutators
   * are handed: the Report View reads that slug off the router, whose params are
   * set from an effect, so it is `""` before that effect runs and still the
   * OUTGOING workspace during a switch. The slug hydration is called with comes
   * from the detail route's `clientLoader` params, which cannot lag.
   *
   * Not observable: bookkeeping about a read, nothing renders it.
   */
  private snapshotSortSlugByReport = new Map<string, string>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // `as never`: the overrides type only admits public keys, and the slug map
    // above is bookkeeping rather than state.
    makeAutoObservable(this, { rootStore: false, snapshotSortSlugByReport: false } as never);
  }

  /**
   * Open the report builder. Pass a `reportId` to open it in edit mode for that
   * report (the row "Edit" action, issue 1675); omit it for the create flow.
   * `opts.step` opens the builder on a specific step (0-based) — the Report
   * View's "Edit filters" entry passes `{ step: 2 }` to land on the dedicated
   * Filters step (#3185); Create and the row "Edit" default to the first step.
   * `opts.fromDetail` marks an edit launched from the Report View so save offers
   * a single "Save & view" that re-runs (issue 1985).
   */
  openBuilder(reportId?: string, opts?: { step?: number; fromDetail?: boolean }): void {
    this.builderOpen = true;
    this.builderReportId = reportId ?? null;
    this.builderInitialStep = opts?.step ?? 0;
    this.builderFromDetail = opts?.fromDetail ?? false;
  }

  /** Close the report builder (consumed by issue 1676). */
  closeBuilder(): void {
    this.builderOpen = false;
    this.builderReportId = null;
    this.builderInitialStep = 0;
    this.builderFromDetail = false;
  }

  async fetchReports(slug: string): Promise<void> {
    this.loadingReports = true;
    this.errorReports = null;
    try {
      const page = await this.rootStore.reportService.list(slug);
      runInAction(() => {
        this.reports.clear();
        for (const r of page.results) this.reports.set(r.id, r);
        // The response's own total, not `reports.size`: the two agree today
        // (an owner-private list against 100-row pages) and this is the one
        // that stays right if that ever stops being true.
        this.reportsTotalCount = page.total_count;
      });
    } catch (err) {
      console.error("report.store: fetchReports failed", { slug, err });
      runInAction(() => {
        this.errorReports = err as Error;
      });
    } finally {
      runInAction(() => {
        this.loadingReports = false;
      });
    }
  }

  async fetchReport(slug: string, id: string): Promise<void> {
    this.loadingReport.set(id, true);
    this.errorReport.set(id, null);
    this.reportNotFound.set(id, false);
    try {
      const report = await this.rootStore.reportService.retrieve(slug, id);
      runInAction(() => {
        this.reports.set(report.id, report);
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        // Not found, or someone else's report — the BE queryset is scoped to the
        // owner, so a non-owner gets a 404. Expected, not an error: flag it so the
        // Report View renders the not-found / no-access state instead of erroring.
        console.warn("report.store: fetchReport 404 (not found or no access)", { slug, id });
        runInAction(() => {
          this.reportNotFound.set(id, true);
          this.reports.delete(id);
        });
      } else {
        console.error("report.store: fetchReport failed", { slug, id, err });
        runInAction(() => {
          this.errorReport.set(id, err as Error);
        });
      }
    } finally {
      runInAction(() => {
        this.loadingReport.set(id, false);
      });
    }
  }

  /**
   * Fetch the field catalog for a set of object types and cache it. The builder
   * re-requests as the user changes the selected sources; the cache avoids a
   * round-trip when revisiting a previously fetched combination.
   */
  async fetchFields(slug: string, objectTypes: ReportObjectType[]): Promise<void> {
    const key = fieldsKey(objectTypes);
    this.loadingFields.set(key, true);
    this.errorFields.set(key, null);
    try {
      const resp = await this.rootStore.reportService.fetchFields(slug, objectTypes);
      runInAction(() => {
        this.fieldsByObjectTypes.set(key, resp);
      });
    } catch (err) {
      console.error("report.store: fetchFields failed", { slug, objectTypes, err });
      runInAction(() => {
        this.errorFields.set(key, err as Error);
      });
    } finally {
      runInAction(() => {
        this.loadingFields.set(key, false);
      });
    }
  }

  /**
   * Every write below takes the `source` of the surface that made it, for its
   * analytics event. It defaults to `page`, the resource's own page; any other
   * surface passes its own. Each event fires after the write succeeds, never on
   * failure.
   */
  async createReport(
    slug: string,
    body: ReportCreateBody,
    source: AnalyticsSource = "page",
  ): Promise<Report> {
    try {
      const created = await this.rootStore.reportService.create(slug, body);
      runInAction(() => {
        this.reports.set(created.id, created);
      });
      track("report_created", {
        report_id: created.id,
        object_types: created.object_types,
        source,
      });
      return created;
    } catch (err) {
      console.error("report.store: createReport failed", { slug, body, err });
      throw err;
    }
  }

  async updateReport(
    slug: string,
    id: string,
    body: ReportUpdateBody,
    source: AnalyticsSource = "page",
  ): Promise<Report> {
    // Read before the write replaces it: `fields` is what this save changed.
    const fields = changedReportFields(body, this.reports.get(id));
    try {
      const updated = await this.rootStore.reportService.update(slug, id, body);
      runInAction(() => {
        this.reports.set(updated.id, updated);
      });
      track("report_updated", { report_id: id, fields, source });
      return updated;
    } catch (err) {
      console.error("report.store: updateReport failed", { slug, id, body, err });
      throw err;
    }
  }

  async deleteReport(slug: string, id: string, source: AnalyticsSource = "page"): Promise<void> {
    try {
      await this.rootStore.reportService.deleteReport(slug, id);
      runInAction(() => {
        this.reports.delete(id);
      });
      track("report_deleted", { report_id: id, source });
    } catch (err) {
      console.error("report.store: deleteReport failed", { slug, id, err });
      throw err;
    }
  }

  async duplicateReport(
    slug: string,
    id: string,
    source: AnalyticsSource = "page",
  ): Promise<Report> {
    try {
      const created = await this.rootStore.reportService.duplicate(slug, id);
      runInAction(() => {
        this.reports.set(created.id, created);
      });
      track("report_duplicated", { report_id: created.id, duplicated_from_id: id, source });
      return created;
    } catch (err) {
      console.error("report.store: duplicateReport failed", { slug, id, err });
      throw err;
    }
  }

  // ---- Run + snapshot actions (Report View, issue 1678) --------------------

  /** Active sort for a report's snapshot (undefined → the run's default order).
   *
   *  Also what the Report View's CSV export sends (`?sort=`), and the export
   *  endpoints validate it exactly as `snapshot` does. They need no guard of
   *  their own: the Export button is disabled until a snapshot page has loaded
   *  successfully, and `loadSnapshot` has by then dropped any sort the run cannot
   *  serve — so an export can only ever carry a sort the run just accepted. */
  snapshotSort(id: string): { sort: string; order: "asc" | "desc" } | undefined {
    return this.snapshotSortByReport.get(id);
  }

  // ---- Persisted snapshot sort (NG-517) -----------------------------------

  /**
   * Scoped to `<workspaceSlug>/<reportId>`, not the workspace: a stored sort
   * names a column of ONE report's resolved snapshot, so carrying it to another
   * report would sort by a column that run may not have.
   */
  private snapshotSortKey(slug: string, id: string): TablePrefsKey {
    return { tableId: SNAPSHOT_TABLE_ID, scope: `${slug}/${id}` };
  }

  /**
   * The ONE place a report's snapshot sort is persisted — it derives what to
   * write from the map rather than from its caller, so a mutator only has to say
   * "this report's sort changed".
   *
   * Not a MobX reaction over the derived value (the shape the projects/intake
   * lists use): the state here is a map keyed by report and the storage key
   * carries that same report id, so a reaction's effect — which receives a
   * value, not a key — would have to diff previous against next to work out
   * WHICH report changed, and the two mutators are the whole surface. The reason
   * that shape exists there does not apply: those keys are fed by nine and
   * thirteen independent filter observables, where wiring the write to one
   * mutator left the others diverging.
   *
   * An unhydrated report has no known workspace, so there is no key worth
   * writing — only reachable before the detail route's `clientLoader` has run.
   */
  private persistSnapshotSort(id: string): void {
    // Behind `persisted-table-prefs` (NG-517) — this store reaches
    // `writeTablePref` directly, so the hook-side gate does not cover it.
    if (!tablePrefsEnabledNow(this.rootStore.featureFlags)) return;
    const slug = this.snapshotSortSlugByReport.get(id);
    if (!slug) return;
    const active = this.snapshotSortByReport.get(id);
    writeTablePref(
      this.snapshotSortKey(slug, id),
      "sorting",
      // A cleared sort is stored as `[]`, not left behind: `readTablePref`
      // distinguishes empty from absent, so a stale entry would come back.
      active ? [{ id: active.sort, desc: active.order === "desc" }] : [],
    );
  }

  /**
   * Restore a report's stored snapshot sort, and record the workspace its key
   * belongs to.
   *
   * Called from the detail route's `clientLoader` BEFORE the `loadSnapshot` in
   * the same loader, so that first request already carries the restored sort —
   * the snapshot is sorted server-side, so hydrating any later would mean an
   * unsorted page followed by a sorted one.
   *
   * No hydrated-once guard, unlike the projects list: the state this writes is
   * keyed by report id exactly as the storage key is, so the failure that guard
   * exists for (global state restored under a per-workspace key, so A → B → A
   * runs A's list on B's entity ids) cannot arise here. Every in-session change
   * writes through, so re-reading on a re-mount lands on the value memory
   * already holds.
   *
   * A stored `[]` — what a re-run leaves behind — restores nothing rather than
   * clearing the current sort, so this is safe to call over a live view.
   */
  hydrateSnapshotSort(slug: string, id: string): void {
    // An empty slug or id would build the scope `/<id>` (or `<slug>/`), a bucket
    // nothing ever reads again. The view holds both only once its route params
    // are set, so this is a state it really passes through, not a defensive
    // check.
    if (!slug || !id) return;
    // Behind `persisted-table-prefs` (NG-517) — see `persistSnapshotSort`.
    // Bailing BEFORE the slug is recorded leaves persistence unarmed too, so
    // the two halves of the gate cannot disagree.
    if (!tablePrefsEnabledNow(this.rootStore.featureFlags)) return;
    this.snapshotSortSlugByReport.set(id, slug);
    const stored = readTablePref<SortingState>(
      this.snapshotSortKey(slug, id),
      "sorting",
      [],
      isSortingState,
    );
    const first = stored[0];
    if (!first) return;
    this.snapshotSortByReport.set(id, {
      sort: first.id,
      order: first.desc ? "desc" : "asc",
    });
  }

  /** True when the report's loaded run is still generating (pending/running). */
  isRunInProgress(id: string): boolean {
    return runInProgress(this.runByReport.get(id));
  }

  /**
   * Trigger a fresh run, then load its snapshot. If the run is queued async
   * (`pending`/`running`), `loadSnapshot` starts polling until it settles. Sort
   * is reset to the run default — a new run re-resolves columns.
   */
  async runReport(slug: string, id: string, source: AnalyticsSource = "page"): Promise<void> {
    this.runningReport.set(id, true);
    this.errorSnapshot.set(id, null);
    try {
      const run = await this.rootStore.reportService.runReport(slug, id);
      runInAction(() => {
        this.runByReport.set(id, run);
        this.snapshotNeverRun.set(id, false);
        this.snapshotSortByReport.delete(id);
      });
      // The run was accepted, even when it is queued: what follows only loads
      // its rows, and a failure there is a read failure, not a failed run.
      track("report_run", { report_id: id, source });
      // Clear the stored sort too, or the next mount hydrates the sort this run
      // just discarded — naming a column the new run may not have resolved.
      this.persistSnapshotSort(id);
      await this.loadSnapshot(slug, id);
    } catch (err) {
      console.error("report.store: runReport failed", { slug, id, err });
      runInAction(() => {
        this.errorSnapshot.set(id, err as Error);
      });
    } finally {
      runInAction(() => {
        this.runningReport.set(id, false);
      });
    }
  }

  /**
   * Set the snapshot sort and reload the first page. Server-side: the whole
   * snapshot is re-sorted, then paged from the start.
   */
  async setSnapshotSort(
    slug: string,
    id: string,
    sort: string,
    order: "asc" | "desc",
  ): Promise<void> {
    runInAction(() => {
      this.snapshotSortByReport.set(id, { sort, order });
    });
    this.persistSnapshotSort(id);
    await this.loadSnapshot(slug, id);
  }

  /**
   * Load the first snapshot page for a report (using the active sort). On a 404
   * (never run) flags `snapshotNeverRun` so the view shows the run prompt rather
   * than an error. While the loaded run is in progress, schedules a poll.
   *
   * `isSortRetry` marks the one self-healing retry described below. Callers never
   * pass it.
   */
  async loadSnapshot(slug: string, id: string, isSortRetry = false): Promise<void> {
    this.loadingSnapshot.set(id, true);
    this.errorSnapshot.set(id, null);
    const sort = this.snapshotSortByReport.get(id);
    const params: SnapshotParams = {
      perPage: SNAPSHOT_PER_PAGE,
      sort: sort?.sort ?? null,
      order: sort?.order ?? null,
    };
    try {
      const resp = await this.rootStore.reportService.fetchSnapshot(slug, id, params);
      runInAction(() => {
        this.runByReport.set(id, resp.run);
        this.snapshotRowsByReport.set(id, resp.rows.results);
        this.snapshotTotalByReport.set(id, resp.rows.total_count);
        this.snapshotHasMoreByReport.set(id, resp.rows.next_page_results);
        this.snapshotCursorByReport.set(
          id,
          resp.rows.next_page_results ? resp.rows.next_cursor : null,
        );
        this.snapshotNeverRun.set(id, false);
      });
      this.scheduleRunPoll(slug, id, resp.run);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        // Never run yet — not an error; surface the run prompt instead.
        runInAction(() => {
          this.snapshotNeverRun.set(id, true);
          this.runByReport.delete(id);
          this.snapshotRowsByReport.delete(id);
          this.snapshotTotalByReport.delete(id);
          this.snapshotHasMoreByReport.delete(id);
          this.snapshotCursorByReport.delete(id);
        });
      } else if (status === 400 && !isSortRetry && this.snapshotSortByReport.has(id)) {
        // Usually: the sort we sent names a column the LATEST RUN's snapshot
        // does not have, so the BE rejects the request (`ParseError` → 400, see
        // `reporting/views.py` `snapshot`). Reachable through ordinary use, and
        // once persisted it is STICKY: the sort is re-read from storage on every
        // mount, so the report stays wedged on its error panel across reloads —
        // the only escape being a full re-run. Drop the dead sort from memory AND
        // from storage, then load once more unsorted, so the user lands on a
        // usable table instead.
        //
        // The condition is ANY 400 while a sort is set — the BE does not say
        // which param it rejected, so an unrelated 400 (a rejected cursor, a
        // malformed param) also spends the stored sort. Accepted: the blast
        // radius is one preference, the retry lands the user on a usable table
        // either way, and telling the cases apart would mean parsing error
        // prose. Narrower than "any failure with a sort set" on purpose,
        // though: a 5xx or a dropped connection says nothing about the sort,
        // and discarding the user's stored sort over a transient outage would
        // be a silent preference loss plus a second request against an
        // already-failing API.
        //
        // What a 400 here is NOT: a run that has not settled. The BE validates a
        // sort only against a COMPLETE snapshot (NG-632) — a latest run that is
        // pending, running or failed has no columns, and used to reject every
        // sort, which read here as "stale" and cost the user a valid preference
        // for the length of someone else's async re-run. That request now
        // succeeds with the sort untouched; the run poll re-sends it, and only a
        // sort the settled run genuinely cannot serve reaches this branch.
        console.warn("report.store: snapshot rejected the restored sort — retrying unsorted", {
          slug,
          id,
          sort: this.snapshotSortByReport.get(id),
          err,
        });
        runInAction(() => {
          this.snapshotSortByReport.delete(id);
        });
        // Storage too: memory alone leaves the next mount to re-hydrate the same
        // dead sort and repeat the 400.
        this.persistSnapshotSort(id);
        // `isSortRetry` bounds this to ONE extra request. The `has(id)` test
        // above already fails on the way back through (the sort is gone), but
        // only until something re-sets it mid-flight — a header click or the run
        // poll landing during the await — and an unbounded retry chain against a
        // failing endpoint is not a failure mode worth leaving to timing.
        await this.loadSnapshot(slug, id, true);
      } else {
        console.error("report.store: loadSnapshot failed", { slug, id, err });
        runInAction(() => {
          this.errorSnapshot.set(id, err as Error);
        });
      }
    } finally {
      runInAction(() => {
        this.loadingSnapshot.set(id, false);
      });
    }
  }

  /**
   * Append the next snapshot page (infinite scroll, issue 1942). No-op when the
   * loaded page is the last (`snapshotHasMore` false / no cursor) or a load-more
   * is already in flight — the Report View wires this to an intersection
   * observer that can fire repeatedly while the sentinel is on-screen. Rows are
   * appended (the first page stays put); a failure surfaces via `errorSnapshot`
   * (the table shows an inline retry) without discarding the loaded rows.
   */
  async loadMoreSnapshot(slug: string, id: string): Promise<void> {
    const cursor = this.snapshotCursorByReport.get(id);
    if (!this.snapshotHasMoreByReport.get(id) || !cursor) return;
    if (this.loadingMoreSnapshot.get(id)) return;
    runInAction(() => {
      this.loadingMoreSnapshot.set(id, true);
    });
    const sort = this.snapshotSortByReport.get(id);
    const params: SnapshotParams = {
      cursor,
      perPage: SNAPSHOT_PER_PAGE,
      sort: sort?.sort ?? null,
      order: sort?.order ?? null,
    };
    try {
      const resp = await this.rootStore.reportService.fetchSnapshot(slug, id, params);
      runInAction(() => {
        const existing = this.snapshotRowsByReport.get(id) ?? [];
        this.snapshotRowsByReport.set(id, [...existing, ...resp.rows.results]);
        this.snapshotTotalByReport.set(id, resp.rows.total_count);
        this.snapshotHasMoreByReport.set(id, resp.rows.next_page_results);
        this.snapshotCursorByReport.set(
          id,
          resp.rows.next_page_results ? resp.rows.next_cursor : null,
        );
      });
    } catch (err) {
      console.error("report.store: loadMoreSnapshot failed", { slug, id, cursor, err });
      runInAction(() => {
        this.errorSnapshot.set(id, err as Error);
      });
    } finally {
      runInAction(() => {
        this.loadingMoreSnapshot.set(id, false);
      });
    }
  }

  /**
   * While a run is `pending`/`running`, re-fetch the snapshot after a delay so
   * the view shows rows the moment it completes. A no-op for a settled run.
   * One timer per report; re-scheduling clears the previous.
   */
  private scheduleRunPoll(slug: string, id: string, run: ReportRun): void {
    const existing = this.pollTimers.get(id);
    if (existing) clearTimeout(existing);
    this.pollTimers.delete(id);
    if (!runInProgress(run)) return;
    const timer = setTimeout(() => {
      this.pollTimers.delete(id);
      void this.loadSnapshot(slug, id);
    }, RUN_POLL_INTERVAL_MS);
    this.pollTimers.set(id, timer);
  }

  /** Stop polling a report's run (call on Report View unmount). */
  stopRunPoll(id: string): void {
    const existing = this.pollTimers.get(id);
    if (existing) clearTimeout(existing);
    this.pollTimers.delete(id);
  }
}
